import { AUTH_TOKEN_KEYS } from 'src/constants/authToken';
import { USER_ROLES_BY_NAME, USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { parseJwt } from 'src/core/services/jwt';
import { preRefreshAccessToken } from 'src/core/services/api/httpClient';
import { api } from 'src/api';
const isReal = v => Boolean(v) && v !== 'null' && v !== 'undefined';
const STAFF = [USER_ROLES_BY_NAME.LivableAdmin, USER_ROLES_BY_NAME.LivableSuperAdmin];
const claimsOf = token => {
  var _parseJwt;
  return (_parseJwt = parseJwt(token !== null && token !== void 0 ? token : '')) !== null && _parseJwt !== void 0 ? _parseJwt : {};
};

/**
 * The company the app has selected: the stored id, else the one company whose name exactly matches
 * the name the header shows (a staff login clears the id and keeps the name). Never a guess: a name
 * that matches no company, or several, comes back as named but unresolved.
 */
async function selectedCompany() {
  var _localStorage$getItem, _res$items;
  const id = localStorage.getItem(USER_SETTINGS_KEYS.company);
  if (isReal(id)) return {
    id,
    named: true
  };
  const name = (_localStorage$getItem = localStorage.getItem(USER_SETTINGS_KEYS.companyName)) === null || _localStorage$getItem === void 0 ? void 0 : _localStorage$getItem.trim();
  if (!name || name === 'Select account') return {
    id: null,
    named: false
  };
  const res = await api.AdminApi.getCompaniesList({
    page: 1,
    pageSize: 50,
    search: name
  }, {});
  const exact = ((_res$items = res === null || res === void 0 ? void 0 : res.items) !== null && _res$items !== void 0 ? _res$items : []).filter(c => {
    var _c$companyName;
    return ((_c$companyName = c.companyName) === null || _c$companyName === void 0 ? void 0 : _c$companyName.trim().toLowerCase()) === name.toLowerCase();
  });
  return {
    id: exact.length === 1 ? exact[0].companyId : null,
    named: true
  };
}
const selection = () => "".concat(localStorage.getItem(USER_SETTINGS_KEYS.company), "|").concat(localStorage.getItem(USER_SETTINGS_KEYS.companyName));
let inflight = null;

/**
 * Puts a staff session back on the company the app has selected. True when the token is on it, or
 * when there is nothing to restore (not staff, nothing selected); false when a company is selected
 * (by id or by a name that does not resolve to exactly one company) and the token is not on it.
 *
 * Records the resolved id as the selection and refreshes; the refresh itself switches the session
 * back to it (httpClient refreshKeepingCompany), so there is one implementation of the switch.
 * Callers for the same selection share one attempt, and a selection that changes during the name
 * lookup is never overwritten with the old one.
 */
export function restoreSelectedCompany() {
  var _inflight;
  const started = selection();
  if (((_inflight = inflight) === null || _inflight === void 0 ? void 0 : _inflight.selection) === started) return inflight.attempt;
  const attempt = (async () => {
    const tokenCompany = () => claimsOf(localStorage.getItem(AUTH_TOKEN_KEYS.accessToken))['custom:companyid'];
    if (!STAFF.includes(claimsOf(localStorage.getItem(AUTH_TOKEN_KEYS.accessToken))['custom:role'])) return true;
    try {
      const selected = await selectedCompany();
      if (selection() !== started) return false;
      if (!selected.id) return !selected.named;
      // Re-read after the lookup: a refresh or another tab may have replaced the token meanwhile.
      if (tokenCompany() === selected.id) return true;
      localStorage.setItem(USER_SETTINGS_KEYS.company, selected.id);
      await preRefreshAccessToken();
      return tokenCompany() === selected.id;
    } catch (_unused) {
      return false;
    }
  })().finally(() => {
    var _inflight2;
    if (((_inflight2 = inflight) === null || _inflight2 === void 0 ? void 0 : _inflight2.attempt) === attempt) inflight = null;
  });
  inflight = {
    selection: started,
    attempt
  };
  return attempt;
}